import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, PasswordInput, Button, Title, Text, Alert, Stack } from '@mantine/core';
import { supabase } from '../lib/supabase';
import Layout from '../components/Layout/Layout';
import { PASSWORD_HINT, passwordProblem } from '../lib/passwordRules';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isValidToken, setIsValidToken] = useState(false);
  const navigate = useNavigate();

  // Check if user has a valid password reset session
  useEffect(() => {
    const checkSession = async () => {
      try {
        // First, check if we have hash params (Supabase uses hash for tokens)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');
        const accessToken = hashParams.get('access_token');
        
        console.log('🔍 Checking reset session...');
        console.log('Type:', type);
        console.log('Has access token:', !!accessToken);
        
        // If we have a recovery token in the hash, Supabase should handle it automatically
        if (type === 'recovery' && accessToken) {
          console.log('🔑 Recovery token found in hash, waiting for Supabase to process...');
          // Give Supabase a moment to process the hash params
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        
        // Check for current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('❌ Session error:', sessionError);
          setError('Invalid or expired reset link. Please request a new password reset.');
          setIsValidToken(false);
          return;
        }
        
        if (!session) {
          console.error('❌ No session found');
          setError('This reset link is invalid or expired, or was opened in a different browser than the one you requested it from. Please request a new one and open it on the same device.');
          setIsValidToken(false);
          return;
        }
        
        console.log('✅ Valid password reset session for:', session.user.email);
        setIsValidToken(true);
        
      } catch (err) {
        console.error('❌ Error checking session:', err);
        setError('An error occurred. Please try again or request a new password reset link.');
        setIsValidToken(false);
      }
    };
    
    checkSession();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess('✅ Password updated successfully! Redirecting to login...');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/register?tab=login');
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout
      title="Reset Password - Prix du Canada"
      description="Reset your Prix du Canada account password"
    >
      <section className="section" style={{background: 'var(--light-gray)', paddingTop: '4rem', paddingBottom: '4rem'}}>
        <div className="container">
          <div style={{ 
            maxWidth: '500px', 
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}>
            <Paper 
              radius="md" 
              p="xl" 
              withBorder
              style={{
                background: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <Title
                order={2}
                align="center"
                mb="md"
                style={{
                  fontFamily: "'Playfair Display', 'Georgia', serif",
                  textAlign: 'center',
                  marginBottom: '0.5rem',
                  color: 'var(--charcoal)'
                }}
              >
                Reset Your Password
              </Title>

              <Text
                size="md"
                style={{
                  textAlign: 'center',
                  color: 'var(--soft-gray)',
                  marginBottom: '2rem'
                }}
              >
                Enter your new password below
              </Text>

              {error && (
                <Alert color="red" mb="md" styles={{ root: { textAlign: 'left' } }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert color="green" mb="md" styles={{ root: { textAlign: 'left' } }}>
                  {success}
                </Alert>
              )}

              {isValidToken ? (
                <form onSubmit={handleResetPassword}>
                  <Stack gap="md">
                    <PasswordInput
                      required
                      label="New Password"
                      placeholder={PASSWORD_HINT}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      styles={{
                        root: { textAlign: 'left' },
                        label: { fontWeight: 600, color: 'var(--charcoal)', textAlign: 'left', display: 'block', width: '100%' },
                        innerInput: { paddingRight: 36 }
                      }}
                    />

                    <PasswordInput
                      required
                      label="Confirm New Password"
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      styles={{
                        root: { textAlign: 'left' },
                        label: { fontWeight: 600, color: 'var(--charcoal)', textAlign: 'left', display: 'block', width: '100%' },
                        innerInput: { paddingRight: 36 }
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      loading={loading}
                      style={{
                        background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                        height: '48px',
                        fontWeight: 600,
                        marginTop: '0.5rem'
                      }}
                    >
                      Reset Password
                    </Button>
                  </Stack>
                </form>
              ) : (
                <Text size="sm" color="dimmed" align="center">
                  Validating reset link...
                </Text>
              )}
            </Paper>

            <Text 
              size="sm" 
              align="center" 
              style={{ color: 'var(--soft-gray)' }}
            >
              Remember your password?{' '}
              <Text
                component="span"
                style={{ color: 'var(--primary-gold)', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigate('/register?tab=login')}
              >
                Sign In
              </Text>
            </Text>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ResetPassword;
